// src/components/Hero.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Hero.css";
import { CONFIG } from "../config";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

const IMAGES_BASE = CONFIG.IMAGES_BASE;

function isNonEmptyString(v) {
  return typeof v === "string" && v.trim() !== "";
}

function buildImageSrcFromValue(candidate) {
  if (!isNonEmptyString(candidate)) return "";
  if (/^https?:\/\//i.test(candidate) || /^\/\//.test(candidate)) return candidate;
  if (candidate.startsWith("/")) return candidate;
  return `${IMAGES_BASE}/${encodeURIComponent(candidate)}`;
}

function buildImageSrc(item) {
  return buildImageSrcFromValue(item?.image || item?.img || "");
}

function buildPhoneSrc(item) {
  return buildImageSrcFromValue(
    item?.phoneImage || item?.mobileImage || item?.phone || ""
  );
}

function renderTitle(title) {
  if (!title) return "";
  return String(title)
    .split("\n")
    .map((line, idx, arr) => (
      <React.Fragment key={idx}>
        {line}
        {idx !== arr.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
}

export default function Hero({
  slides,
  fontStyles,
  heroColors = {},
  heroSettings = {},
  autoPlay = true,
  intervalMs = 5000,
  headingStyleOverride,
  contentStyleOverride,
  buttonStyleOverride,
}) {
  // ✅ Always define SLIDES (no default content)
  const SLIDES = useMemo(() => (Array.isArray(slides) ? slides : []), [slides]);
  const isMobile = useIsMobile();

  // ✅ Hooks MUST always run (no early return before this)
  const [index, setIndex] = useState(0);
  const [isHover, setIsHover] = useState(false);
  const autoRef = useRef(null);

  // keep index safe if slides length changes
  useEffect(() => {
    setIndex((prev) => {
      if (!SLIDES.length) return 0;
      return Math.min(prev, SLIDES.length - 1);
    });
  }, [SLIDES.length]);

  // ✅ Preload images (only if slides exist)
  useEffect(() => {
    if (!SLIDES.length) return;
    SLIDES.forEach((s) => {
      const src = buildImageSrc(s);
      if (!src) return;
      const img = new Image();
      img.src = src;
    });
  }, [SLIDES]);

  // ✅ Autoplay (only if slides > 1)
  useEffect(() => {
    if (isHover) return;
    if (!autoPlay) return;
    if (SLIDES.length <= 1) return;

    autoRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, Math.max(1000, Number(intervalMs) || 5000));

    return () => {
      if (autoRef.current) clearInterval(autoRef.current);
    };
  }, [isHover, SLIDES.length, autoPlay, intervalMs]);

  const goTo = (i) => {
    if (!SLIDES.length) return;
    setIndex((i + SLIDES.length) % SLIDES.length);
  };

  const getColor = (slideColor, sectionColor, globalColor, fallback) => {
    if (heroSettings?.useSectionColors === false) {
      return globalColor || fallback;
    }
    return slideColor || sectionColor || globalColor || fallback;
  };

  // ✅ AFTER hooks: if no slides, render nothing
  if (!SLIDES.length) return null;

  return (
    <section
      className="hero"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className="hero-track">
        {SLIDES.map((s, i) => {
          const isActive = i === index;

          const alignment = s?.contentAlignment || "left";
          const sizeOverrides = s?.sectionStyles?.fontSizes || {};

          const titleColor = getColor(
            s?.titleColor,
            heroColors?.headingColor,
            fontStyles?.heading?.color,
            "#ffffff"
          );
          const contentColor = getColor(
            s?.contentColor,
            heroColors?.contentColor || heroColors?.subHeadingColor,
            fontStyles?.content?.color || fontStyles?.subHeading?.color,
            "#ffffff"
          );
          const buttonColor = getColor(
            s?.buttonColor,
            heroColors?.buttonTextColor,
            fontStyles?.button?.color,
            "#ffffff"
          );
          const buttonBgColor = getColor(
            s?.buttonBgColor,
            heroColors?.buttonBgColor,
            fontStyles?.button?.backgroundColor,
            "transparent"
          );

          const buttonHoverColor = s?.buttonHoverColor || "#111827";
          const buttonHoverBgColor = s?.buttonHoverBgColor || "#ffffff";
          const overlayOpacity = Number(s?.overlayOpacity ?? 0.35);

          const titleStyle = {
            color: titleColor,
            fontFamily: fontStyles?.heading?.fontFamily || "inherit",
            fontSize: adjustFontSize(
              sizeOverrides.heading || fontStyles?.heading?.fontSize || undefined,
              isMobile
            ),
            fontWeight: fontStyles?.heading?.fontWeight || "900",
            lineHeight: fontStyles?.heading?.lineHeight || "1.06",
            textAlign: alignment,
          };

          const contentStyle = {
            color: contentColor,
            fontFamily: fontStyles?.content?.fontFamily || "inherit",
            fontSize: adjustFontSize(
              sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
              isMobile
            ),
            fontWeight: fontStyles?.content?.fontWeight || "400",
            lineHeight: fontStyles?.content?.lineHeight || "1.55",
            textAlign: alignment,
          };

          const buttonStyle = {
            color: buttonColor,
            backgroundColor: buttonBgColor,
            fontFamily: fontStyles?.button?.fontFamily || "inherit",
            fontWeight: fontStyles?.button?.fontWeight || "700",
            fontSize: adjustFontSize(
              sizeOverrides.button || fontStyles?.button?.fontSize || "12px",
              isMobile
            ),
            "--button-hover-color": buttonHoverColor,
            "--button-hover-bg": buttonHoverBgColor,
          };
          
          const finalTitleStyle = {
            ...titleStyle,
            ...(headingStyleOverride || {}),
          };
          const finalContentStyle = {
            ...contentStyle,
            ...(contentStyleOverride || {}),
          };
          const finalButtonStyle = {
            ...buttonStyle,
            ...(buttonStyleOverride || {}),
          };

          const imgSrc = buildImageSrc(s);
          const phoneSrc = buildPhoneSrc(s);

          return (
            <article key={i} className={`hero-slide ${isActive ? "active" : ""}`}>
              {imgSrc ? (
                phoneSrc ? (
                  <picture>
                    <source media="(max-width: 700px)" srcSet={phoneSrc} />
                    <img src={imgSrc} alt={s?.title || ""} className="hero-img" />
                  </picture>
                ) : (
                  <img src={imgSrc} alt={s?.title || ""} className="hero-img" />
                )
              ) : (
                <div className="hero-img hero-img-fallback" />
              )}

              <div
                className="hero-overlay"
                style={{
                  background: `linear-gradient(
                    90deg,
                    rgba(0, 0, 0, ${overlayOpacity}) 0%,
                    rgba(0, 0, 0, ${overlayOpacity * 0.55}) 50%,
                    rgba(0, 0, 0, 0) 100%
                  )`,
                }}
              >
                <div className="hero-content" style={{ textAlign: alignment }}>
                  {s?.title ? (
                    <h2 className="hero-title" style={finalTitleStyle}>
                      <span className="hero-title-bg">{renderTitle(s.title)}</span>
                    </h2>
                  ) : null}

                  {s?.content ? (
                    <p className="hero-sub" style={finalContentStyle}>
                      {s.content}
                    </p>
                  ) : null}

                  {s?.buttonname ? (
                    <Link className="hero-cta" to={s?.buttonlink || "#"} style={finalButtonStyle}>
                      {s.buttonname}
                    </Link>
                  ) : null}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {SLIDES.length > 1 && (
        <div className="hero-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`hero-dot ${i === index ? "active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              type="button"
            />
          ))}
        </div>
      )}
    </section>
  );
}
